import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, History, Loader2, Lock, RefreshCw, Settings2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useRegion } from "@/contexts/RegionContext";
import { setCollectionsRegion } from "@/lib/regionFmt";
import {
  getCollectionReportMonth, getCollectionReportSummary, getCollectionReportLive, lockCollectionReportDay,
  getCollectionReportLockStatus, editCollectionReportRow, importCollectionReportXlsx, downloadCollectionReportXlsx,
  rebuildCollectionReportHistory, downloadCollectionReportDifferences,
} from "@/lib/api";
import { ReportMonthGrid } from "./ReportMonthGrid";
import { ReportSideTables } from "./ReportSideTables";
import { ReportSummary } from "./ReportSummary";
import { ImportPreviewDialog } from "./ImportPreviewDialog";
import { ReportSettingsDialog } from "./ReportSettingsDialog";
import { MONTH_SHORT, apiError, dateTime, gbp2 } from "./reportFormat";

const LIVE_POLL_MS = 5 * 60 * 1000;

// Collection Report tab — the team's monthly sheet, fed by LoanDisk and (UK) the bank feed / (AU) GoCardless.
// Month tabs + Summary, like the workbook. Follows the collections desk UK | AU toggle.
export default function CollectionReportTab() {
  const { user, isAdmin } = useAuth();
  const { currentRegion } = useRegion();
  const aus = currentRegion === "AUS";
  setCollectionsRegion(currentRegion);   // module flag → api interceptor (?region=) + money formatting
  const canEdit = typeof isAdmin === "function" ? isAdmin() : false;
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [view, setView] = useState(today.getMonth() + 1); // 1-12 or "summary"
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockStatus, setLockStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [rebuildBusy, setRebuildBusy] = useState(false);
  const fileRef = useRef(null);
  const pendingFile = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (view === "summary") setSummary(await getCollectionReportSummary(year));
      else setData(await getCollectionReportMonth(year, view));
      setLockStatus(await getCollectionReportLockStatus().catch(() => null));
    } catch (e) {
      if (!silent) toast.error("Could not load the collection report");
    } finally { setLoading(false); }
  }, [year, view, currentRegion]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  // Today's cash moves with every bank pull / GoCardless confirmation — re-read while the tab is open (current month only).
  useEffect(() => {
    const isCurrent = view !== "summary" && year === today.getFullYear() && view === today.getMonth() + 1;
    if (!isCurrent) return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") load(true); }, LIVE_POLL_MS);
    return () => clearInterval(t);
  }, [load, view, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCell = async (date, key, value, note) => {
    try {
      await editCollectionReportRow(date, { [key]: value }, note, user?.name || user?.email);
      toast.success("Saved");
      load(true);
    } catch (e) { toast.error(apiError(e, "Could not save")); throw e; }
  };

  const lockToday = async (force = false) => {
    setLockBusy(true);
    try {
      const res = await lockCollectionReportDay({ force, actor: user?.name || user?.email });
      if (res.already_running) { toast.info("A lock is already running — give it a minute"); return; }
      toast.success("Locking today's row from LoanDisk — takes about two minutes");
      const poll = setInterval(async () => {
        const s = await getCollectionReportLockStatus().catch(() => null);
        if (s && !s.running) {
          clearInterval(poll); setLockBusy(false); setLockStatus(s);
          const r = s.result || {};
          if (r.lock_skipped) toast.info(`Today's row was already locked (${r.lock_reason}). Use Re-lock to overwrite.`);
          else toast.success(`Today's row locked${r.finalised?.length ? ` · cash fixed for ${r.finalised.join(", ")}` : ""}`);
          load(true);
        }
      }, 5000);
    } catch (e) { toast.error(apiError(e, "Lock failed")); setLockBusy(false); }
  };

  const refreshLive = async () => {
    try {
      const l = await getCollectionReportLive();
      toast.success(l.final ? "Today is already final" : `${aus ? "GoCardless" : "Bank feed"}: ${gbp2(l.cash_live)} received so far${aus && l.cash_live_pending ? ` · ${gbp2(l.cash_live_pending)} in flight` : ""}`);
      load(true);
    } catch { toast.error(aus ? "GoCardless unavailable" : "Bank feed unavailable"); }
  };

  const onImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!aus) {
      try { const r = await importCollectionReportXlsx(f); toast.success(`Imported ${r.inserted} new, ${r.updated} updated, ${r.filled} blanks filled, ${r.skipped} kept (${r.first} → ${r.last})`); load(true); }
      catch (err) { toast.error(apiError(err, "Import failed")); }
      return;
    }
    try { pendingFile.current = f; setPreview(await importCollectionReportXlsx(f, { dryRun: true })); }
    catch (err) { toast.error(apiError(err, "Could not read the workbook")); }
  };

  const confirmImport = async () => {
    setImportBusy(true);
    try {
      const r = await importCollectionReportXlsx(pendingFile.current);
      toast.success(`Imported ${r.inserted} new, ${r.updated} updated, ${r.filled} blanks filled, ${r.skipped} kept (${r.first} → ${r.last})`);
      setPreview(null); load(true);
    } catch (err) { toast.error(apiError(err, "Import failed")); }
    finally { setImportBusy(false); }
  };

  const rebuild = async () => {
    setRebuildBusy(true);
    try {
      const r = await rebuildCollectionReportHistory(`${year}-01-01`);
      toast.success(`Portal check done: ${r.days_checked} days · ${r.blanks_filled} blanks filled · ${r.differences.length} day${r.differences.length === 1 ? "" : "s"} differ from the sheet`);
      load(true);
    } catch (err) { toast.error(apiError(err, "Rebuild failed")); }
    finally { setRebuildBusy(false); }
  };

  const onExport = async () => {
    try {
      const blob = await downloadCollectionReportXlsx(year);
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url;
      a.download = `${year} Collection Report${aus ? " - SimplyFunded Australia" : ""}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const onDifferences = async () => {
    try {
      const blob = await downloadCollectionReportDifferences(year);
      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `${year} AU collection report - portal check.csv`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const lr = lockStatus?.result;
  return (
    <div className="space-y-4" data-testid="collection-report-tab" data-region={currentRegion}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-1 flex-wrap" data-testid="report-month-tabs">
          {aus && <span className="mr-1 inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 ring-1 ring-amber-200" data-testid="report-region-chip">🇦🇺 AU · AUD</span>}
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="h-7 rounded-md border border-slate-200 bg-white px-2 text-xs font-semibold" data-testid="report-year-select">
            {[today.getFullYear() - 1, today.getFullYear(), today.getFullYear() + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          {MONTH_SHORT.map((m, i) => (
            <button key={m} type="button" onClick={() => setView(i + 1)} data-testid={`report-tab-${i + 1}`}
              className={`h-7 rounded-md px-2.5 text-xs font-medium transition-colors ${view === i + 1 ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>{m}</button>
          ))}
          <button type="button" onClick={() => setView("summary")} data-testid="report-tab-summary"
            className={`h-7 rounded-md px-2.5 text-xs font-semibold transition-colors ${view === "summary" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>Summary</button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={refreshLive} data-testid="report-refresh-live"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />{aus ? "Refresh GoCardless" : "Refresh live cash"}</Button>
          {canEdit && (
            <Button size="sm" className="h-7 text-xs bg-slate-900 hover:bg-slate-800" onClick={() => lockToday(false)} disabled={lockBusy} data-testid="report-lock-today">
              {lockBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Lock className="w-3.5 h-3.5 mr-1.5" />}Lock today
            </Button>
          )}
          {canEdit && <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={() => lockToday(true)} disabled={lockBusy} title="Overwrite today's locked figures from LoanDisk" data-testid="report-relock-today">Re-lock</Button>}
          {canEdit && <><input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onImport} data-testid="report-import-input" />
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => fileRef.current?.click()} data-testid="report-import-xlsx"><Upload className="w-3.5 h-3.5 mr-1.5" />Import sheet</Button></>}
          {canEdit && aus && (
            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={rebuild} disabled={rebuildBusy} title="Rebuild every historic day from LoanDisk AU + GoCardless, fill blanks and flag differences against the sheet" data-testid="report-rebuild-history">
              {rebuildBusy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <History className="w-3.5 h-3.5 mr-1.5" />}Portal check {year}
            </Button>
          )}
          {aus && <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500" onClick={onDifferences} title="CSV of days where the portal's LoanDisk/GoCardless figure differs from the sheet" data-testid="report-differences-csv">Differences</Button>}
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onExport} data-testid="report-export-xlsx"><Download className="w-3.5 h-3.5 mr-1.5" />Export {year}</Button>
          {canEdit && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500" onClick={() => setSettingsOpen(true)} title="Model target and team closures" data-testid="report-settings"><Settings2 className="w-3.5 h-3.5" /></Button>}
        </div>
      </div>

      <p className="text-[11px] text-slate-500" data-testid="report-status-line">
        {aus
          ? <>Rows lock on the AU desk's first <span className="font-medium">Sync from LoanDisk</span> of the Sydney day (Due today + P2Ps, arrears from day 1, open-loans book). Today's cash is live from GoCardless as charges confirm; yesterday's is fixed from LoanDisk AU's posted repayments with the Direct Debit / PayTo / online split — Friday's row absorbs the weekend and NSW public holidays. Days marked <span className="rounded bg-stone-200/70 px-1 text-[9px] font-semibold uppercase text-stone-600">sheet</span> come from the team's workbook (cash logged on payout Fridays, so Mon–Thu read low).</>
          : <>Rows lock when the team presses <span className="font-medium">Sync from LoanDisk</span> on Loan Arrears Ageing (Due today + P2Ps, arrears from day 1, open-loans book); yesterday's cash is fixed from LoanDisk's posted repayments at the same moment — Friday's row absorbs the weekend.</>}
        {lockStatus?.at && <> Last run {dateTime(lockStatus.at)} by {lockStatus.actor}{lr?.finalised?.length ? ` · fixed ${lr.finalised.join(", ")}` : ""}{lr?.lock_errors && Object.keys(lr.lock_errors).length ? <span className="text-rose-600"> · errors: {Object.keys(lr.lock_errors).join(", ")}</span> : null}.</>}
      </p>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500" data-testid="report-loading"><Loader2 className="w-5 h-5 animate-spin" /><span className="ml-3 text-sm">Loading…</span></div>
      ) : view === "summary" ? (
        <ReportSummary data={summary} onOpenMonth={(m) => setView(m)} />
      ) : (
        <>
          <ReportMonthGrid data={data} canEdit={canEdit} onSaveCell={saveCell} />
          <ReportSideTables data={data} />
        </>
      )}
      <ImportPreviewDialog preview={preview} busy={importBusy} onConfirm={confirmImport} onClose={() => setPreview(null)} />
      <ReportSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} aus={aus} year={year} onSaved={() => load(true)} />
    </div>
  );
}
